// TableRow.jsx

import React, { useCallback, useState } from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getEnumOptionColor, getEnumOptionIconKey, getEnumOptionLabel } from 'utilities/EnumOptions';
import { normalizeUrlForOpen } from 'utilities/Url';
import { getUrlString } from 'utilities/UrlFieldValue';
import {
    formatFieldValue,
    getFieldType,
    isInlineEditable,
    resolveFieldValue,
} from 'views/table/utils/tableFieldUtils';

// CSS
import './TableRow.css';

// Icons
import Icon from 'svg/Icon';

// Components
import EnumPillSelect from 'components/enumpillselect/EnumPillSelect';
import Pill from 'components/pill/Pill';
import ProfileStack from 'components/profilestack/ProfileStack';
import TableCellEditor from 'views/table/components/TableCellEditor';
import RowHeader from 'views/table/rowheader/RowHeader';

/**
 * TableRow
 *
 * @returns {ReactNode} The rendered component.
 */
const TableRow = ({
    node,
    columns,
    schemaFieldMap,
    fieldDataSection,
    editingCell,
    editingValue,
    setEditingValue,
    handleCommitEdit,
    handleCancelEdit,
    handleCellActivate,
    handleOpenNode,
    onNodeChange = null,
    typeKey = '',
    selectedKeys,
    onToggleSelect,
    showCheckboxes = true,
    language = 'en',
    resolveNodelistTitle,
    draggable = false,
    onDragStart = null,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [hoveredUrlFieldKey, setHoveredUrlFieldKey] = useState('');
    const [isRowHovered, setIsRowHovered] = useState(false);

    // ----- HANDLERS ----- //

    /**
 * Handles open url.
 *
 * @param {*} rawUrl
 */

    const handleOpenUrl = useCallback((rawUrl) => {
        const targetUrl = normalizeUrlForOpen(getUrlString(rawUrl) || rawUrl);
        if (!targetUrl) return;
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }, []);
    const handleRowDragStart = useCallback((event) => {
        if (!draggable || !onDragStart) return;
        const target = event?.target;
        if (target?.closest?.('input, textarea, button, a, [contenteditable="true"]')) {
            event.preventDefault();
            return;
        }
        if (editingCell?.nodeKey === node?.key) {
            event.preventDefault();
            return;
        }
        onDragStart(event);
    }, [draggable, editingCell?.nodeKey, node?.key, onDragStart]);

    // ----- RENDER ----- //

    return (
        <div
          className="table-view-row"
          draggable={draggable}
          onDragStart={draggable ? handleRowDragStart : undefined}
          onMouseEnter={() => setIsRowHovered(true)}
          onMouseLeave={() => setIsRowHovered(false)}
        >
            {columns.map((column) => {
                if (column.key === 'open') {
                    const isSelected = selectedKeys?.has?.(node?.key);
                    return (
                        <RowHeader
                          key={`table_cell_${column.key}`}
                          selected={isSelected}
                          showCheckboxes={showCheckboxes}
                          isRowHovered={isRowHovered}
                          onOpen={() => handleOpenNode(node)}
                          onToggleSelect={() => onToggleSelect?.(node?.key)}
                        />
                    );
                }

                const fieldDef = schemaFieldMap.get(column.key) || null;
                const resolvedValue = column.key === 'title'
                    ? node?.title
                    : resolveFieldValue(node, fieldDef, fieldDataSection);
                const value = column.key === 'title'
                    ? resolvedValue
                    : formatFieldValue(resolvedValue, fieldDef, column.key, { resolveNodelistTitle });
                const isEditing = editingCell?.nodeKey === node?.key
                    && editingCell?.fieldKey === column.key;
                const fieldType = getFieldType(fieldDef, column.key);

                const isUrl = fieldType === 'url';
                const isEnum = isEnumCompatibleFieldType(fieldType);
                const isMultiselect = fieldType === 'multiselect';
                const multiselectPills = isMultiselect
                    ? (Array.isArray(resolvedValue) ? resolvedValue : [resolvedValue])
                        .map((entry, index) => {
                            if (entry == null) return null;
                            const normalizedValue = typeof entry === 'string' ? entry.trim() : String(entry).trim();
                            if (!normalizedValue) return null;

                            return {
                                color: getEnumOptionColor(fieldDef?.options || [], normalizedValue),
                                iconKey: getEnumOptionIconKey(fieldDef?.options || [], normalizedValue),
                                key: `${column.key}-${normalizedValue}-${index}`,
                                label: getEnumOptionLabel(fieldDef?.options || [], normalizedValue, language) || normalizedValue,
                            };
                        })
                        .filter(Boolean)
                    : [];
                const isTimestamp = column.key === 'dateCreated' || column.key === 'dateModified';
                const isAutonumber = column.key === 'autonumber';
                const isAutokey = column.key === 'autokey';
                const isInline = Boolean(isInlineEditable(fieldDef, column.key));
                const canEditEnumPill = isEnum
                    && typeof onNodeChange === 'function';
                const isModalField = fieldType === 'multiselect'
                    || fieldType === 'tags'
                    || fieldType === 'percentage'
                    || fieldType === 'date'
                    || fieldType === 'daterange'
                    || fieldType === 'datetime'
                    || fieldType === 'datetimerange'
                    || fieldType === 'location'
                    || fieldType === 'people'
                    || fieldType === 'relation'
                    || fieldType === 'checklist';

                const peopleProfiles = fieldType === 'people'
                    ? (Array.isArray(resolvedValue) ? resolvedValue : [])
                        .map((entry) => {
                            const key = String(entry?.userKey || entry?.key || '').trim();
                            if (!key) return null;
                            const name = entry?.data_profile?.name
                                || entry?.name
                                || entry?.title
                                || entry?.email
                                || key;

                            return {
                                key,
                                name,
                                title: name,
                                coverUrl: entry?.coverUrl || '',
                                data_profile: {
                                    name,
                                    email: entry?.email || entry?.data_profile?.email || '',
                                },
                            };
                        })
                        .filter(Boolean)
                    : [];

                const checklistSummary = fieldType === 'checklist'
                    ? (() => {
                        const totalItems = Number(resolvedValue?.totalItems) || 0;
                        const completedItems = Number(resolvedValue?.completedItems) || 0;
                        if (totalItems > 0) {
                            return `${completedItems}/${totalItems}`;
                        }
                        const items = (Array.isArray(resolvedValue) ? resolvedValue : [])
                            .filter((entry) => entry && typeof entry === 'object' && String(entry?.text || '').trim().length > 0);
                        if (items.length === 0) return '';
                        const doneCount = items.filter((entry) => (
                            Boolean(entry?.checked) || String(entry?.status || '').toLowerCase() === 'done'
                        )).length;
                        return `${doneCount}/${items.length}`;
                    })()
                    : '';
                const cursorStyle = isAutonumber || isAutokey || isTimestamp
                    ? 'default'
                    : (isModalField ? 'pointer' : (isInline ? 'text' : undefined));

                return (
                    <div
                      className="table-view-cell"
                      style={{
                        width: column.width || 200,
                      }}
                      key={`table_cell_${column.key}`}
                      onClick={() => {
                            if (!isEditing && !canEditEnumPill) {
                                handleCellActivate(node, fieldDef, column.key);
                            }
                        }}
                    >
                        {isEditing ? (
                            <TableCellEditor
                              fieldType={fieldType}
                              fieldDef={fieldDef}
                              value={editingValue}
                              onCancel={handleCancelEdit}
                              onChange={setEditingValue}
                              onCommit={() => handleCommitEdit(node, fieldDef, column.key)}
                            />
                        ) : (
                            <div
                              className="table-view-cell-value"
                              style={cursorStyle ? { cursor: cursorStyle } : undefined}
                            >
                                {isUrl ? (
                                    <div className="table-view-url-cell">

                                        {/* URL TEXT */}
                                        <div className="table-view-url-text">
                                            {value}
                                        </div>

                                        {/* URL OPEN */}
                                        {value ? (
                                            <button
                                              className="table-view-url-open"
                                              style={{
                                                    backgroundColor: hoveredUrlFieldKey === column.key ? theme.backgroundHover : 'transparent',
                                                }}
                                              type="button"
                                              aria-label="Open url"
                                              onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleOpenUrl(resolvedValue);
                                                }}
                                              onMouseEnter={() => setHoveredUrlFieldKey(column.key)}
                                              onMouseLeave={() => setHoveredUrlFieldKey('')}
                                            >
                                                <Icon
                                                  iconKey="open_in_new"
                                                  color={theme.foregroundMuted}
                                                  size={18}
                                                />
                                            </button>
                                        ) : null}
                                    </div>
                                ) : isEnum ? (
                                    <EnumPillSelect
                                      field={fieldDef}
                                      value={resolvedValue == null ? '' : String(resolvedValue).trim()}
                                      node={node}
                                      typeKey={typeKey || node?.type}
                                      canEdit={canEditEnumPill}
                                      onNodeChange={onNodeChange}
                                      placeholder={`${fieldDef?.label || fieldDef?.name || 'Select'}…`}
                                      language={language}
                                      fontSize="12px"
                                    />
                                ) : isMultiselect ? (
                                    multiselectPills.length > 0 ? (
                                        <div className="table-view-pill-list">
                                            {multiselectPills.map((pill) => (
                                                <Pill
                                                  className="table-view-pill"
                                                  key={pill.key}
                                                  color={pill.color || theme.foregroundMuted}
                                                  iconKey={pill.iconKey}
                                                  label={pill.label}
                                                  labelColor={theme.highlightForeground}
                                                />
                                            ))}
                                        </div>
                                    ) : null
                                ) : fieldType === 'people' ? (
                                    peopleProfiles.length > 0 ? (
                                        <div className="table-view-people-stack" title={peopleProfiles.map((p) => p?.name || p?.title || '').filter(Boolean).join(', ')}>
                                            <ProfileStack
                                              profiles={peopleProfiles}
                                              size={20}
                                              maxDisplay={4}
                                              allowNavigation={false}
                                            />
                                        </div>
                                    ) : null
                                ) : fieldType === 'checklist' ? (
                                    checklistSummary
                                ) : (
                                    value
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// Dev Tools
TableRow.displayName = 'TableRow';

export default TableRow;
